import React, { memo, useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';

import { Box, Typography } from '@mui/material';
import FormGenerator from '@sharedCR/common/formGenerator';
import { FormNames } from 'app/services/autoLockForms/autoLockForms.constants';
import _ from 'lodash';

import { getBorrowerFullName } from 'utils/loanApplication';

import { selectLoanApplicationSubjectPropertyState } from 'store/loanApplications/selectors';

import { BorrowersTestIds } from '../../../../../Borrowers.testIds';
import { pagesPropTypes } from '../../MainContent.constants';

import { getPoaConfig } from './POA.config';
import { POA_CONTACT_HINT } from './POA.constants';
import { hintStyles, wrapperStyles } from './POA.styles';
import {
  getDefaultPoaDesignationByState,
  getNormalizedSuffix,
  getPoaCapacityLabel,
  getPoaDesignationOption,
  getPoaDesignationValue,
  getPoaVerbiage,
  replacePoaCapacityInVerbiage,
  replacePoaNameInVerbiage,
} from './POA.utils';

const POA = ({
  changedFields,
  isLoading,
  isSaving,
  form,
  validationErrors,
  validateField,
  handleChange,
  entityId,
  entityData,
  section,
  setEntityConfig,
  setInForm,
  setForm,
  setInitialFormValues,
  className,
  pendingChanges,
  disabledPendingChangesHighlight,
}) => {
  const config = useMemo(() => getPoaConfig({ entityId, section }), [entityId, section]);

  const subjectPropertyState = useSelector(selectLoanApplicationSubjectPropertyState);
  const defaultPoaDesignationOption = useMemo(
    () => getPoaDesignationOption(getDefaultPoaDesignationByState(subjectPropertyState)),
    [subjectPropertyState],
  );

  const borrowerFormData = form?.[entityId];
  const currentPoaDesignation = borrowerFormData?.powerOfAttorney?.designation;
  const currentPoaName = borrowerFormData?.powerOfAttorney?.name;
  const currentPoaVerbiage = borrowerFormData?.powerOfAttorney?.verbiage;
  const currentPoaDesignationValue = getPoaDesignationValue(currentPoaDesignation);
  const isCurrentPoaDesignationEmpty = _.isNil(currentPoaDesignationValue);
  const hasBorrowerPowerOfAttorney = !_.isNil(borrowerFormData?.powerOfAttorney);

  const borrowerFullName = useMemo(
    () =>
      getBorrowerFullName({
        firstName: entityData?.firstName,
        middleName: entityData?.middleName,
        lastName: entityData?.lastName,
        suffix: getNormalizedSuffix(entityData?.suffix),
      }),
    [entityData?.firstName, entityData?.middleName, entityData?.lastName, entityData?.suffix],
  );

  const previousPoaNameRef = useRef(null);
  const previousCapacityLabelRef = useRef(null);
  const hasSeededDefaultPoaDesignationRef = useRef(false);

  useEffect(() => {
    setEntityConfig(config);
  }, [config, setEntityConfig]);

  useEffect(() => {
    if (hasSeededDefaultPoaDesignationRef.current) {
      return;
    }

    if (!hasBorrowerPowerOfAttorney) {
      return;
    }

    if (_.isNil(defaultPoaDesignationOption)) {
      return;
    }

    hasSeededDefaultPoaDesignationRef.current = true;

    if (!isCurrentPoaDesignationEmpty) {
      return;
    }

    const seedDefaultPoaDesignation = prev => ({
      ...prev,
      [entityId]: {
        ...prev[entityId],
        powerOfAttorney: {
          ...prev[entityId]?.powerOfAttorney,
          designation: defaultPoaDesignationOption,
        },
      },
    });

    setInitialFormValues(seedDefaultPoaDesignation);
    setForm(seedDefaultPoaDesignation);
  }, [
    defaultPoaDesignationOption,
    entityId,
    hasBorrowerPowerOfAttorney,
    isCurrentPoaDesignationEmpty,
    setForm,
    setInitialFormValues,
  ]);

  useEffect(() => {
    if (!currentPoaName || !currentPoaDesignationValue || !borrowerFullName) {
      return;
    }

    const nextCapacityLabel = getPoaCapacityLabel(currentPoaDesignation);

    if (!nextCapacityLabel) {
      return;
    }

    const previousPoaName = previousPoaNameRef.current;
    const previousCapacityLabel = previousCapacityLabelRef.current;

    previousPoaNameRef.current = currentPoaName;
    previousCapacityLabelRef.current = nextCapacityLabel;

    if (!currentPoaVerbiage) {
      const nextVerbiage = getPoaVerbiage({
        borrowerFullName,
        poaName: currentPoaName,
        poaCapacityLabel: nextCapacityLabel,
      });

      if (!nextVerbiage) {
        return;
      }

      setInForm(`[${entityId}].powerOfAttorney.verbiage`, nextVerbiage, false);

      return;
    }

    const verbiageWithPoaName = replacePoaNameInVerbiage({
      verbiage: currentPoaVerbiage,
      previousPoaName,
      nextPoaName: currentPoaName,
    });

    const nextVerbiage = replacePoaCapacityInVerbiage({
      verbiage: verbiageWithPoaName,
      previousCapacityLabel,
      nextCapacityLabel,
    });

    if (nextVerbiage === currentPoaVerbiage) {
      return;
    }

    setInForm(`[${entityId}].powerOfAttorney.verbiage`, nextVerbiage, false);
  }, [
    borrowerFullName,
    currentPoaDesignation,
    currentPoaDesignationValue,
    currentPoaName,
    currentPoaVerbiage,
    entityId,
    setInForm,
  ]);

  if (!config) {
    return null;
  }

  return (
    <Box className={className} sx={wrapperStyles}>
      <FormGenerator
        isNestedForm
        changedFields={changedFields}
        config={config}
        disabledPendingChangesHighlight={disabledPendingChangesHighlight}
        form={form}
        formName={FormNames.POA}
        handleChange={handleChange}
        isLoading={isSaving || isLoading}
        numberOfColumns={4}
        pendingChanges={pendingChanges}
        setInForm={setInForm}
        validateField={validateField}
        validationErrors={validationErrors}
      />
      <Typography
        color="text.secondary"
        data-testid={BorrowersTestIds.POA_FORM_POA_EMAIL_PHONE_HINT}
        sx={hintStyles}
        variant="regular12"
      >
        {POA_CONTACT_HINT}
      </Typography>
    </Box>
  );
};

POA.propTypes = pagesPropTypes;

export default memo(POA);
